import React, { useState, useEffect, useRef } from 'react';
import { Plus, Shuffle, Trash2, X, ChevronDown, ChevronUp, Clock, Upload, Download, RotateCcw, EyeOff, Scale, Copy, CheckCheck } from 'lucide-react';
import ImportModal from './ImportModal';
import { useLang } from '../utils/i18n';
import { UNDO_TIMEOUT_MS } from '../utils/constants';
import { randomInt } from '../utils/random';
import { useAnnouncer } from '../hooks/useAnnouncer';
import type { WheelEntry } from '../types/wheel';
import { downloadResultsCsv, resultsText } from '../utils/results';
import { PALETTES, type PaletteName } from '../utils/palettes';
import CoachMark from './CoachMark';
import ImageUploadButton from './ImageUploadButton';
import { MAX_ENTRIES } from '../utils/limits';
import { MAX_ENTRY_NAME_LENGTH } from '../utils/wheelData';
import {
  MIN_WEIGHT, MAX_WEIGHT, DEFAULT_WEIGHT,
  clampWeight, formatWeight, isActive, isUniform, totalWeight, probabilities,
  parseBulkLine, serializeBulk, buildExport,
} from '../utils/weights';

interface EntriesPanelProps {
  names: string[];
  weights: number[];
  onWeightChange: (index: number, weight: number) => void;
  setNames: (names: string[]) => void;
  /** Append imported entries, carrying their weights. */
  onImportEntries: (entries: { name: string; weight: number }[]) => void;
  /** Append locally processed image-backed entries. */
  onAddImageEntries: (entries: WheelEntry[]) => void;
  /** Per-entry segment colours; an empty slot means "use the palette default". */
  colors: (string | undefined)[];
  /** Pass undefined to clear the override and go back to the palette. */
  onColorChange: (index: number, color: string | undefined) => void;
  /** Full entries, so Undo can snapshot and restore them atomically. */
  entries: WheelEntry[];
  /** Apply a permutation of indexes — keeps weight and colour with their name. */
  onReorder: (order: number[]) => void;
  /** Restore a complete snapshot in one update. */
  onRestoreEntries: (entries: WheelEntry[]) => void;
  /**
   * Also record the snapshot in the page-level undo stack, so Ctrl+Z can
   * reach these actions. The panel keeps its own toast — this is purely an
   * additional consumer, not a replacement.
   */
  onSnapshot?: (entries: WheelEntry[], label: string) => void;
  isSpinning: boolean;
  results: { name: string; time: string }[];
  onClearResults: () => void;
  /** Named in the exported file and its filename, so a saved record says which draw it was. */
  wheelTitle: string;
  /** Keeps the list dots in step with the palette the wheel is drawing. */
  palette: PaletteName;
}



type Tab = 'list' | 'bulk' | 'results';

const EntriesPanel: React.FC<EntriesPanelProps> = ({
  names, weights, onWeightChange, setNames, onImportEntries, onAddImageEntries, colors, onColorChange,
  entries, onReorder, onRestoreEntries, onSnapshot, isSpinning, results, onClearResults, wheelTitle, palette,
}) => {
  const COLORS = PALETTES[palette] ?? PALETTES.classic;
  const { t } = useLang();
  const { announce } = useAnnouncer();
  const [tab, setTab] = useState<Tab>('list');
  const [localText, setLocalText] = useState(() => serializeBulk(names, weights));
  const [newEntry, setNewEntry] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [showProbabilities, setShowProbabilities] = useState(false);
  // Weight editing is off by default. A number input on every row is clutter
  // for the large majority who just want to pick a name, and it made the list
  // read like a spreadsheet. Revealed by the Weights button in the footer — and
  // automatically whenever weights are already in play, so a shared or saved
  // weighted wheel never arrives with its own controls hidden.
  const [showWeights, setShowWeights] = useState(false);
  const [copiedResults, setCopiedResults] = useState(false);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const weightsBtnRef = useRef<HTMLButtonElement>(null);
  const bulkDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (copyTimer.current) clearTimeout(copyTimer.current);
    if (bulkDebounceRef.current) clearTimeout(bulkDebounceRef.current);
  }, []);

  const handleCopyResults = async () => {
    try {
      await navigator.clipboard.writeText(resultsText(results, wheelTitle));
      setCopiedResults(true);
      if (copyTimer.current) clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopiedResults(false), 2000);
    } catch { /* clipboard denied — the CSV button is the fallback */ }
  };
  const [undoState, setUndoState] = useState<{
    // A full snapshot, not parallel name/weight arrays. Restoring those
    // separately meant replaying weights on top of a name list, which could not
    // survive a reorder and never carried colours at all.
    entries: WheelEntry[];
    label: string;
    timeoutId: ReturnType<typeof setTimeout> | null;
  } | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Mirror external changes (template load, import, undo) into the textarea — but
  // never while the user is typing in it. `names` is a fresh array on every parent
  // render, so this effect fires constantly; without the focus guard it rewrites
  // in-progress text, swallowing trailing newlines and half-typed "Alice x2".
  useEffect(() => {
    if (typeof document !== 'undefined' && document.activeElement === textareaRef.current) return;
    setLocalText(serializeBulk(names, weights));
  }, [names, weights]);

  const applyNames = (updated: string[]) => {
    setNames(updated);
    setLocalText(serializeBulk(updated, weights));
  };

  const parseBulkText = (text: string): WheelEntry[] => {
    // Match image metadata by label occurrence instead of position. Deleting
    // the first bulk-edit line must not move its image onto the next entry;
    // duplicate labels consume their images in the same order as the list.
    const imageQueues = new Map<string, string[]>();
    for (const entry of entries) {
      if (!entry.imageDataUrl) continue;
      const queue = imageQueues.get(entry.name) ?? [];
      queue.push(entry.imageDataUrl);
      imageQueues.set(entry.name, queue);
    }

    return text
      .split('\n')
      .map(line => line.trim())
      .filter(Boolean)
      .slice(0, MAX_ENTRIES)
      .map(parseBulkLine)
      .filter(parsed => parsed.name.trim())
      .map((parsed, index) => {
        const name = parsed.name.trim().slice(0, MAX_ENTRY_NAME_LENGTH);
        const previous = entries[index];
        const imageDataUrl = imageQueues.get(name)?.shift();
        return {
          name,
          weight: parsed.weight,
          ...(previous?.color ? { color: previous.color } : {}),
          ...(imageDataUrl ? { imageDataUrl } : {}),
        };
      });
  };

  const applyBulkText = (text: string): WheelEntry[] => {
    const nextEntries = parseBulkText(text);
    // Bulk edit replaces the list. Using the import callback here used to
    // append the parsed list after setNames(), duplicating weighted entries.
    onRestoreEntries(nextEntries);
    return nextEntries;
  };

  const handleBulkChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    setLocalText(text);

    if (bulkDebounceRef.current) clearTimeout(bulkDebounceRef.current);
    bulkDebounceRef.current = setTimeout(() => {
      applyBulkText(text);
      bulkDebounceRef.current = null;
    }, 200);
  };

  /**
   * Bulk Edit applies live as the user types, so one undo per keystroke would be
   * useless. Snapshot when editing starts and open a single undo window on blur,
   * covering the whole editing session.
   */
  const bulkSnapRef = useRef<WheelEntry[] | null>(null);

  const handleBulkFocus = () => {
    bulkSnapRef.current = entries.map(e => ({ ...e }));
  };

  const handleBulkBlur = () => {
    const snap = bulkSnapRef.current;
    bulkSnapRef.current = null;
    if (!snap) return;
    if (bulkDebounceRef.current) {
      clearTimeout(bulkDebounceRef.current);
      bulkDebounceRef.current = null;
    }
    // Apply synchronously so a quick type-and-tab cannot leave the last 200 ms
    // of text unapplied or arm Undo against stale React props.
    const nextEntries = applyBulkText(localText);
    // Compare serialized form so a weight-only edit ("Alice x2") still counts.
    const before = serializeBulk(snap.map(e => e.name), snap.map(e => e.weight));
    const after = serializeBulk(nextEntries.map(e => e.name), nextEntries.map(e => e.weight));
    if (before === after) return;
    armUndo(
      snap,
      nextEntries.length === 0
        ? t('entries.bulkCleared')
        : t('entries.bulkCount', { count: nextEntries.length }),
    );
  };

  const handleAdd = () => {
    const val = newEntry.trim();
    if (!val) return;
    applyNames([...names, val]);
    announce(t('entries.addedNamed', { name: val }), 'polite');
    setNewEntry('');
  };

  /**
   * Open a 5s undo window over an already-captured snapshot.
   *
   * Separate from saveAndApply because Bulk Edit applies its change live on every
   * keystroke — the snapshot there is taken when editing begins, not when the
   * undo is armed.
   */
  const armUndo = (prevEntries: WheelEntry[], label: string) => {
    // While a window is already open, keep the ORIGINAL snapshot rather than
    // replacing it. A burst of changes inside the window rewinds to before the
    // burst, never to the middle of it. This matters most when one action
    // triggers another: clicking Shuffle while the Bulk Edit textarea is focused
    // fires blur (arming a bulk-edit undo) and then the shuffle — replacing the
    // snapshot there would silently make the bulk edit unrecoverable.
    const open = undoState !== null;
    const snapshot = open ? undoState.entries : prevEntries;
    // The label must describe what Undo will actually do. Once the window spans
    // two different actions, naming just the latest one would be a lie.
    const nextLabel = open && undoState.label !== label ? t('entries.undoRecent') : label;

    // Feed the page-level stack too. Uses the pre-coalesce snapshot and the
    // original label; that hook does its own burst-merging.
    onSnapshot?.(snapshot, label);

    if (undoState?.timeoutId) clearTimeout(undoState.timeoutId);
    const timeoutId = setTimeout(() => setUndoState(null), UNDO_TIMEOUT_MS);
    setUndoState({ entries: snapshot, label: nextLabel, timeoutId });
  };

  /** Apply a destructive change, snapshotting complete entries for Undo. */
  const saveAndApplyEntries = (nextEntries: WheelEntry[], label: string) => {
    const snapshot = entries.map(e => ({ ...e }));
    onRestoreEntries(nextEntries);
    armUndo(snapshot, label);
  };

  const handleAddImages = (uploaded: WheelEntry[]) => {
    if (uploaded.length === 0) return;
    const snapshot = entries.map(entry => ({ ...entry }));
    onAddImageEntries(uploaded);
    armUndo(
      snapshot,
      t(uploaded.length === 1 ? 'entries.addedImage' : 'entries.addedImages', { count: uploaded.length }),
    );
  };

  const handleUndo = () => {
    if (!undoState) return;
    if (undoState.timeoutId) clearTimeout(undoState.timeoutId);
    // One atomic restore. The previous version applied names first and then
    // replayed weights over the top, which left a frame of mismatched state and
    // could not restore an order the names alone did not describe.
    onRestoreEntries(undoState.entries);
    setLocalText(serializeBulk(
      undoState.entries.map(e => e.name),
      undoState.entries.map(e => e.weight),
    ));
    setUndoState(null);
  };

  const dismissUndo = () => {
    if (undoState?.timeoutId) clearTimeout(undoState.timeoutId);
    setUndoState(null);
  };

  // Don't leave a pending auto-dismiss timer behind when the panel unmounts.
  useEffect(() => () => {
    if (undoState?.timeoutId) clearTimeout(undoState.timeoutId);
  }, [undoState]);

  const handleRemove = (i: number) => {
    saveAndApplyEntries(
      entries.filter((_, idx) => idx !== i),
      t('entries.undoRemoved', { name: names[i] })
    );
    announce(t('entries.undoRemoved', { name: names[i] }), 'polite');
  };
  /**
   * Reorder by permutation, never by rebuilding the name list.
   *
   * Shuffling names alone left each weight and colour at its old index, so a
   * wheel where one name carried weight 5 became one where whoever landed in
   * that slot did. Moving indexes keeps every entry intact.
   */
  const applyOrder = (order: number[], label: string) => {
    const snapshot = entries.map(e => ({ ...e }));
    onReorder(order);
    armUndo(snapshot, label);
  };

  const handleShuffle = () => {
    if (names.length < 2) return; // nothing meaningful to shuffle
    // Fisher–Yates: `sort(() => random() - 0.5)` is not a uniform shuffle and
    // leaves entries near their original positions far more often than chance.
    const order = names.map((_, i) => i);
    for (let i = order.length - 1; i > 0; i--) {
      const j = randomInt(i + 1);
      [order[i], order[j]] = [order[j], order[i]];
    }
    applyOrder(order, t('entries.undoShuffled'));
  };

  const handleSort = () => {
    if (names.length < 2) return;
    const order = names
      .map((name, i) => ({ name, i }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
      .map(x => x.i);
    applyOrder(order, t('entries.undoSorted'));
  };
  const handleClear = () => {
    saveAndApplyEntries(
      [],
      t(names.length === 1 ? 'entries.undoCleared' : 'entries.undoClearedPlural', { count: names.length })
    );
    announce(t('entries.allCleared'), 'polite');
  };

  const handleImport = (imported: { name: string; weight: number }[]) => {
    // Goes through the parent so the imported weights survive. Routing it
    // through applyNames would drop them: setNames only carries weights over
    // positionally from the entries that were already there.
    onImportEntries(imported);
  };

  const handleExport = () => {
    const { content, filename, mime } = buildExport(names, weights);
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Weight summary ──
  const total = totalWeight(weights);
  const weighted = !isUniform(weights);
  const probs = probabilities(weights);
  const inactiveCount = weights.filter(w => !isActive(w)).length;
  // `weighted` misses the all-zero case (uniform, but nothing is selectable),
  // so check for inactive entries too — those rows must always show why they
  // are greyed out and offer a way back.
  const weightsVisible = showWeights || weighted || inactiveCount > 0;

  const commitWeight = (index: number, raw: string) => {
    // An empty or unparseable field resets to 1 rather than silently becoming
    // 0, which would take the entry off the wheel without the user asking.
    onWeightChange(index, raw.trim() === '' ? DEFAULT_WEIGHT : clampWeight(raw));
  };

  return (
    <section aria-label={t('entries.title')} className="relative flex flex-col flex-1 w-full min-h-0 bg-white dark:bg-gray-900 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
      <h2 className="sr-only">{t('entries.title')}</h2>
      {/* Tabs */}
      <div className="flex border-b border-gray-100 dark:border-gray-700">
        {(['list', 'bulk', 'results'] as Tab[]).map(tb => (
          <button
            key={tb}
            onClick={() => setTab(tb)}
            className={`flex-1 py-3 text-xs font-bold uppercase tracking-wider transition-colors relative ${
              tab === tb ? 'text-violet-600 dark:text-violet-400' : 'text-gray-500 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
            }`}
          >
            {tb === 'list' ? t('entries.title') : tb === 'bulk' ? t('entries.bulk') : t('entries.results')}
            {tb === 'list' && names.length > 0 && (
              <span className="ms-1.5 bg-violet-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                {names.length}
              </span>
            )}
            {tb === 'results' && results.length > 0 && (
              <span className="ms-1.5 bg-gray-400 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                {results.length}
              </span>
            )}
            {tab === tb && (
              <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-violet-500 rounded-full" />
            )}
          </button>
        ))}
      </div>

      {/* --- LIST TAB --- */}
      {tab === 'list' && (
        <>
          <div className="px-4 py-3 border-b border-gray-50 dark:border-gray-800">
            <div className="flex gap-2">
              <label htmlFor="add-entry-input" className="sr-only">
                {t('entries.add')}
              </label>
              <input
                id="add-entry-input"
                type="text"
                maxLength={MAX_ENTRY_NAME_LENGTH}
                value={newEntry}
                dir="auto"
                onChange={e => setNewEntry(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAdd()}
                placeholder={t('entries.placeholder')}
                disabled={isSpinning}
                className="min-w-0 flex-1 px-3 py-2 text-sm border border-gray-500 dark:border-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:opacity-50 transition-all"
              />
              <button
                onClick={handleAdd}
                disabled={isSpinning || !newEntry.trim()}
                aria-label={t('entries.add')}
                title={t('entries.add')}
                className="p-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Plus size={16} />
              </button>
              <ImageUploadButton
                disabled={isSpinning || entries.length >= MAX_ENTRIES}
                remainingEntries={Math.max(0, MAX_ENTRIES - entries.length)}
                existingImageDataLength={entries.reduce((sum, entry) => sum + (entry.imageDataUrl?.length ?? 0), 0)}
                onAddEntries={handleAddImages}
              />
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto px-3 py-2" style={{ flex: '1 1 0%', height: 0 }}>
            {names.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full py-10 text-center">
                <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-3">
                  <Plus size={20} className="text-gray-300 dark:text-gray-500" />
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">{t('entries.empty')}</p>
                <p className="text-xs text-gray-300 dark:text-gray-600 mt-1">{t('entries.emptySub')}</p>
              </div>
            ) : (
              <ul role="list" className="space-y-0.5">
                {names.map((name, i) => {
                  const w = weights[i] ?? DEFAULT_WEIGHT;
                  const off = !isActive(w);
                  return (
                    <li
                      key={i}
                      className={`group flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                        off ? 'entry-disabled opacity-50' : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      {/* The colour dot IS the picker. A native <input type="color">
                          is sized to the dot and laid over it: it opens the OS
                          colour panel, which is keyboard-accessible and localised
                          for free, and costs no bundle weight. */}
                      <span className="relative w-3.5 h-3.5 flex-shrink-0">
                        <span
                          aria-hidden="true"
                          className={`block w-3.5 h-3.5 rounded-full border ${
                            colors[i] ? 'border-gray-400 dark:border-gray-300' : 'border-transparent'
                          }`}
                          style={{ backgroundColor: off ? '#cbd5e1' : (colors[i] ?? COLORS[i % COLORS.length]) }}
                        />
                        <input
                          type="color"
                          value={colors[i] ?? COLORS[i % COLORS.length]}
                          disabled={isSpinning}
                          onChange={e => onColorChange(i, e.target.value)}
                          title={t(colors[i] ? 'entries.segmentColorReset' : 'entries.segmentColorSet', { name })}
                          aria-label={t('entries.segmentColorFor', { name })}
                          // Double-click clears the override. Discoverable from the
                          // title, and the only way back to the palette default
                          // once a colour has been set.
                          onDoubleClick={() => onColorChange(i, undefined)}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                        />
                      </span>
                      {entries[i]?.imageDataUrl && (
                        <img
                          src={entries[i].imageDataUrl}
                          alt=""
                          className="h-8 w-8 flex-shrink-0 rounded-lg object-cover ring-1 ring-gray-200 dark:ring-gray-700"
                          onError={event => { event.currentTarget.hidden = true; }}
                        />
                      )}
                      <span dir="auto" className={`flex-1 min-w-0 text-sm truncate ${off ? 'text-gray-500 dark:text-gray-400 line-through' : 'text-gray-700 dark:text-gray-200'}`}>
                        {name}
                      </span>
                      {/* Weight input — hidden until weights are in use or asked for */}
                      {weightsVisible && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {off ? (
                          <span title={t('entries.weightOff')} className="text-gray-500 dark:text-gray-400">
                            <EyeOff size={11} aria-hidden="true" />
                          </span>
                        ) : weighted && (
                          <span className="text-[10px] font-bold bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 px-1.5 py-0.5 rounded-full leading-none">
                            {probs[i].toFixed(probs[i] < 10 ? 1 : 0)}%
                          </span>
                        )}
                        <input
                          type="number"
                          min={MIN_WEIGHT}
                          max={MAX_WEIGHT}
                          step={0.5}
                          value={formatWeight(w)}
                          disabled={isSpinning}
                          onChange={e => commitWeight(i, e.target.value)}
                          onBlur={e => commitWeight(i, e.target.value)}
                          className="w-14 text-center text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-violet-400 disabled:opacity-40 transition-all"
                           title={t('entries.weightHelp')}
                           aria-label={t('entries.weightFor', { name })}
                        />
                      </div>
                      )}
                      <button
                        onClick={() => handleRemove(i)}
                        disabled={isSpinning}
                        aria-label={t('entries.removeNamed', { name })}
                        title={t('entries.remove')}
                        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-violet-50 dark:hover:bg-violet-900/30 rounded-md text-gray-500 dark:text-gray-400 hover:text-violet-500 transition-all disabled:pointer-events-none"
                      >
                        <X size={12} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Weight summary — only meaningful once there is something on the wheel */}
          {names.length > 0 && (
            <div className="px-4 py-2 border-t border-gray-50 dark:border-gray-800 text-[11px] text-gray-500 dark:text-gray-400">
              <div className="flex items-center gap-2 flex-wrap">
                <span>
                  {t('entries.count', { count: names.length })}
                  {inactiveCount > 0 && ` (${t('entries.inactiveCount', { count: inactiveCount })})`}
                </span>
                {/* Only worth stating once weights differ — while they are all 1
                    the total is just the entry count again. */}
                {weightsVisible && (
                  <>
                    <span aria-hidden="true">·</span>
                    <span>
                      {t('entries.totalWeight', { weight: formatWeight(total) })}
                    </span>
                  </>
                )}
                {weighted && (
                  <button
                    type="button"
                    onClick={() => setShowProbabilities(v => !v)}
                    aria-expanded={showProbabilities}
                    aria-controls="entry-probabilities"
                    className="ms-auto flex items-center gap-0.5 font-semibold text-violet-600 dark:text-violet-400 hover:underline"
                  >
                    {t(showProbabilities ? 'entries.hideProbabilities' : 'entries.showProbabilities')}
                    {showProbabilities ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                  </button>
                )}
              </div>

              {weighted && showProbabilities && (
                <ul id="entry-probabilities" role="list" className="mt-2 max-h-28 overflow-y-auto space-y-0.5">
                  {names.map((name, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <bdi className={`flex-1 min-w-0 truncate ${isActive(weights[i] ?? DEFAULT_WEIGHT) ? '' : 'text-gray-500 dark:text-gray-400 line-through'}`}>
                        {name}
                      </bdi>
                      <span className="tabular-nums font-semibold text-gray-700 dark:text-gray-200">
                        {probs[i].toFixed(1)}%
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}

      {/* --- BULK TAB --- */}
      {tab === 'bulk' && (
        <div className="flex-1 min-h-0 flex flex-col px-4 py-3 gap-2" style={{ flex: '1 1 0%', height: 0 }}>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('bulk.hint')}{' '}
            <span className="text-violet-500">{t('entries.bulkWeightTip')}</span>
          </p>
          <textarea
            ref={textareaRef}
            value={localText}
            dir="auto"
            onChange={handleBulkChange}
            onFocus={handleBulkFocus}
            onBlur={handleBulkBlur}
            disabled={isSpinning}
            placeholder={'Alice::3\nBob\nCharlie (2)\nDiana x0.5'}
            className="flex-1 w-full px-3 py-2.5 text-sm border border-gray-500 dark:border-gray-400 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent resize-none font-mono leading-relaxed placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:opacity-50 transition-all"
          />
        </div>
      )}

      {/* --- RESULTS TAB --- */}
      {tab === 'results' && (
        <div className="flex-1 min-h-0 overflow-y-auto px-3 py-2" style={{ flex: '1 1 0%', height: 0 }}>
          {results.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full py-10 text-center">
              <Clock size={28} className="text-gray-200 dark:text-gray-600 mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">{t('entries.noResults')}</p>
              <p className="text-xs text-gray-300 dark:text-gray-600 mt-1">{t('entries.noResultsSub')}</p>
            </div>
          ) : (
            <ul role="list" className="space-y-1 py-1">
              {[...results].reverse().map((r, i) => (
                <li key={i} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <span className="w-6 h-6 rounded-full bg-violet-50 dark:bg-violet-900/30 text-violet-500 dark:text-violet-400 flex items-center justify-center text-xs font-bold flex-shrink-0">
                    {results.length - i}
                  </span>
                  <span dir="auto" className="flex-1 min-w-0 text-sm font-medium text-gray-800 dark:text-white truncate">{r.name}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">{r.time}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700 flex items-center gap-2 flex-wrap">
        {tab !== 'results' ? (
          <>
            <button
              onClick={() => setShowImport(true)}
              disabled={isSpinning}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/20 rounded-lg hover:bg-violet-100 dark:hover:bg-violet-900/40 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <Upload size={12} /> {t('entries.import')}
            </button>
            <button
              onClick={handleExport}
              disabled={isSpinning || names.length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <Download size={12} /> {t('entries.export')}
            </button>
            {/* Reveals the per-entry weight column. Hidden while weights are
                already visible for a reason the user cannot toggle away — the
                button would then do nothing when switched off. */}
            {tab === 'list' && !weighted && inactiveCount === 0 && names.length > 0 && (
              <button
                ref={weightsBtnRef}
                onClick={() => setShowWeights(v => !v)}
                aria-pressed={showWeights}
                title={t('entries.showWeightsHelp')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                  showWeights
                    ? 'text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/20'
                    : 'text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <Scale size={12} /> {t(showWeights ? 'entries.hideWeights' : 'entries.weights')}
              </button>
            )}
            <div className="flex-1" />
            <button
              onClick={handleShuffle}
              disabled={isSpinning || names.length < 2}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <Shuffle size={12} /> {t('entries.shuffle')}
            </button>
            <button
              onClick={handleSort}
              disabled={isSpinning || names.length < 2}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronUp size={11} className="-mb-0.5" /><ChevronDown size={11} className="-mt-0.5 -ms-1.5" /> {t('entries.sort')}
            </button>
            <button
              onClick={handleClear}
              disabled={isSpinning || names.length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <Trash2 size={12} /> {t('entries.clear')}
            </button>
          </>
        ) : (
          <>
            <div className="flex-1 min-w-0 text-xs text-gray-500 dark:text-gray-400">{results.length} {t('entries.spins')} {t('entries.spinsRecorded')}</div>
            {/* A draw that cannot be written down is hard to defend afterwards.
                Copy for pasting into a chat or email, CSV for a record. */}
            <button
              onClick={handleCopyResults}
              disabled={results.length === 0}
              title={t('entries.copyHistory')}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {copiedResults ? <CheckCheck size={12} className="text-green-500" /> : <Copy size={12} />}
              {t(copiedResults ? 'entries.copied' : 'entries.copy')}
            </button>
            <button
              onClick={() => downloadResultsCsv(results, wheelTitle)}
              disabled={results.length === 0}
              title={t('entries.downloadHistory')}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <Download size={12} /> CSV
            </button>
            <button
              onClick={onClearResults}
              disabled={results.length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-violet-500 hover:bg-violet-50 dark:hover:bg-violet-900/20 rounded-lg disabled:opacity-40 transition-colors"
            >
              <Trash2 size={12} /> {t('entries.clear')}
            </button>
          </>
        )}
      </div>

      {/* Undo toast — auto-dismisses after 5s */}
      {undoState && (
        <div
          key={String(undoState.timeoutId)}
          role="status"
          aria-live="polite"
          className="animate-undo-in absolute inset-x-3 bottom-16 z-20 flex items-center gap-2 px-3 py-2.5 rounded-xl shadow-lg bg-gray-900 dark:bg-gray-700 text-white overflow-hidden"
        >
          <span className="flex-1 min-w-0 text-xs font-medium truncate">{undoState.label}</span>
          <button
            onClick={handleUndo}
            className="flex items-center gap-1 px-2.5 py-1 text-xs font-bold text-violet-300 hover:text-white hover:bg-white/10 rounded-lg transition-colors flex-shrink-0"
          >
            <RotateCcw size={12} /> {t('entries.undo')}
          </button>
          <button
            onClick={dismissUndo}
            aria-label={t('entries.undoDismiss')}
            title={t('entries.undoDismiss')}
            className="flex-shrink-0 p-1 text-gray-500 hover:text-gray-300 transition-colors"
          >
            <X size={12} />
          </button>
          {/* 5s countdown bar */}
          <span
            className="animate-shrink absolute bottom-0 start-0 h-0.5 bg-violet-400"
            style={{ animationDuration: `${UNDO_TIMEOUT_MS}ms` }}
            aria-hidden="true"
          />
        </div>
      )}

      {showImport && (
        <ImportModal onClose={() => setShowImport(false)} onImport={handleImport} />
      )}

      {/* Coach mark on the Weights button — appears the first time a user
          has 3+ entries and hasn't yet discovered the feature. */}
      {tab === 'list' && !weighted && inactiveCount === 0 && names.length >= 3 && (
        <CoachMark
          id="weights"
          targetRef={weightsBtnRef}
          message={t('entries.weightsCoach')}
          delay={2000}
          placement="top"
        />
      )}
    </section>
  );
};

export default EntriesPanel;



